/**
 * Local stub replacing @healthinal/ui (private package).
 * @healthinal/ui is a thin wrapper around @mui/joy. This file re-exports
 * everything from Joy UI and provides the custom `createTheme` + `Icons`.
 */

// ─── Joy UI component re-exports ─────────────────────────────────────────────
export {
  Alert,
  Autocomplete,
  AutocompleteOption,
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  CssBaseline,
  CssVarsProvider,
  Divider,
  Dropdown,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Slider,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  styled,
  Switch,
  Tab,
  tabClasses,
  Table,
  TabList,
  Tabs,
  Textarea,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/joy';

export type {
  ButtonProps,
  ChipProps,
  ColorPaletteProp,
  DividerProps,
  ListItemButtonProps,
  TypographyProps,
  VariantProp,
} from '@mui/joy';

// createFilterOptions lives on the Autocomplete sub-module
export { createFilterOptions } from '@mui/joy/Autocomplete';

// ─── createTheme ──────────────────────────────────────────────────────────────
// @healthinal/ui wraps Joy UI's extendTheme with a custom signature that accepts
// a primary hue/saturation as the first argument. We ignore that and just apply
// the standard Joy UI theme options.
import { extendTheme } from '@mui/joy/styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createTheme(_primaryConfig: any, options: Parameters<typeof extendTheme>[0] = {}) {
  return extendTheme(options);
}

// ─── InputSlotsAndSlotProps ───────────────────────────────────────────────────
// Used only for typing the `slotProps.input` of Joy UI's <Input> component.
import type { InputHTMLAttributes } from 'react';
export type InputSlotsAndSlotProps = {
  slotProps?: {
    input?: InputHTMLAttributes<HTMLInputElement> & Record<string, unknown>;
    [key: string]: unknown;
  };
};

// ─── Icons ────────────────────────────────────────────────────────────────────
// @healthinal/ui exports an `Icons` namespace whose members are MUI icon
// components. We map every icon name used in this codebase to the corresponding
// @mui/icons-material component.
import AccessTime from '@mui/icons-material/AccessTime';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Add from '@mui/icons-material/Add';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import AllInbox from '@mui/icons-material/AllInbox';
import Analytics from '@mui/icons-material/Analytics';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import Bloodtype from '@mui/icons-material/Bloodtype';
import BloodtypeOutlined from '@mui/icons-material/BloodtypeOutlined';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Cancel from '@mui/icons-material/Cancel';
import ChangeCircleOutlined from '@mui/icons-material/ChangeCircleOutlined';
import Check from '@mui/icons-material/Check';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Checklist from '@mui/icons-material/Checklist';
import Close from '@mui/icons-material/Close';
import Dangerous from '@mui/icons-material/Dangerous';
import DarkMode from '@mui/icons-material/DarkMode';
import Delete from '@mui/icons-material/Delete';
import Download from '@mui/icons-material/Download';
import Description from '@mui/icons-material/Description';
import Edit from '@mui/icons-material/Edit';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import EventAvailable from '@mui/icons-material/EventAvailable';
import EventBusy from '@mui/icons-material/EventBusy';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import FilterList from '@mui/icons-material/FilterList';
import Flaky from '@mui/icons-material/Flaky';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import Groups from '@mui/icons-material/Groups';
import HelpOutline from '@mui/icons-material/HelpOutline';
import HighlightOff from '@mui/icons-material/HighlightOff';
import History from '@mui/icons-material/History';
import Hub from '@mui/icons-material/Hub';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LightMode from '@mui/icons-material/LightMode';
import Logout from '@mui/icons-material/Logout';
import Map from '@mui/icons-material/Map';
import MedicalInformation from '@mui/icons-material/MedicalInformation';
import MedicalServices from '@mui/icons-material/MedicalServices';
import MedicationLiquid from '@mui/icons-material/MedicationLiquid';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import Notes from '@mui/icons-material/Notes';
import PendingOutlined from '@mui/icons-material/PendingOutlined';
import Person from '@mui/icons-material/Person';
import PersonAdd from '@mui/icons-material/PersonAdd';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import SafetyDivider from '@mui/icons-material/SafetyDivider';
import Science from '@mui/icons-material/Science';
import Search from '@mui/icons-material/Search';
import Sms from '@mui/icons-material/Sms';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import Sync from '@mui/icons-material/Sync';
import Timelapse from '@mui/icons-material/Timelapse';
import Today from '@mui/icons-material/Today';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import UploadFile from '@mui/icons-material/UploadFile';

export const Icons = {
  AccessTime,
  AccountCircle,
  Add,
  AddCircleOutline,
  AllInbox,
  Analytics,
  ArrowBack,
  ArrowForward,
  ArrowRight,
  AssignmentTurnedIn,
  Bloodtype,
  BloodtypeOutlined,
  CalendarMonth,
  Cancel,
  ChangeCircleOutlined,
  Check,
  CheckCircleOutline,
  CheckCircleOutlined,
  Checklist,
  Close,
  Dangerous,
  DarkMode,
  Delete,
  Download,
  Description,
  Edit,
  ErrorOutline,
  EventAvailable,
  EventBusy,
  ExpandLess,
  ExpandMore,
  FileUploadOutlined,
  FilterList,
  Flaky,
  FormatListBulleted,
  Groups,
  HelpOutline,
  HighlightOff,
  History,
  Hub,
  InfoOutlined,
  LightMode,
  Logout,
  Map,
  MedicalInformation,
  MedicalServices,
  MedicationLiquid,
  NavigateBefore,
  NavigateNext,
  Notes,
  PendingOutlined,
  Person,
  PersonAdd,
  // RemoveCircleOutlineOutlined doesn't exist in MUI — mapped to RemoveCircleOutline
  RemoveCircleOutline,
  RemoveCircleOutlineOutlined: RemoveCircleOutline,
  SafetyDivider,
  Science,
  Search,
  Sms,
  SupervisorAccount,
  Sync,
  Timelapse,
  Today,
  UnfoldMore,
  UploadFile,
};
